import type { Dispatch, AnyAction } from 'redux';

import * as RelatedWorkOrdersAPI from 'ab-api/web/temporaryEmployee/findRelatedWorkOrdersTable';

import type TemporaryEmployeeTableVM from 'ab-viewModels/temporaryEmployee/table.viewModel';
import type TemporaryEmployeeVM from 'ab-viewModels/temporaryEmployee/temporaryEmployee.viewModel';
import type TemporaryEmployeeNameAvailableVM from 'ab-viewModels/temporaryEmployee/temporaryEmployeeNameAvailable.viewModel';
import type TemporaryEmployeeOptionVM from 'ab-viewModels/temporaryEmployee/temporaryEmployeeOption.viewModel';

import type { TemporaryEmployeeNameAvailableRM } from 'ab-requestModels/temporaryEmployee.requestModel';
import type UpsertTemporaryEmployeeRM from 'ab-requestModels/temporaryEmployee.requestModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { GetRootState } from 'af-reducers';

import { errorHandler } from 'af-utils/actions.util';
import { http } from 'af-utils/http.util';

import API from 'af-constants/routes/api';

import type { TableContent } from 'ab-common/dataStructures/tableContent';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

export function findById(employeeId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<TemporaryEmployeeVM>(API.V1.RESOURCES.TEMPORARY_EMPLOYEE.FIND_BY_ID(employeeId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllForCompanyTable(tableRequestModel: TableQuery) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			return await http.get<TableContent<TemporaryEmployeeTableVM>>(API.V1.RESOURCES.TEMPORARY_EMPLOYEE.COMPANY_TABLE(data));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllDeletedForCompanyTable(tableRequestModel: TableQuery = {} as TableQuery) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			return await http.get<TableContent<TemporaryEmployeeTableVM>>(API.V1.RESOURCES.TEMPORARY_EMPLOYEE.COMPANY_TABLE_DELETED(data));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

/**
 *
 * @param dueDate 'YYYY-MM-DD'
 */
export function findAllForWorkOrdersForDueDate(dueDate: string) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<TemporaryEmployeeOptionVM[]>(API.V1.RESOURCES.TEMPORARY_EMPLOYEE.FIND_WORK_ORDER_TEMPORARY_EMPLOYEES_BY_DUE_DATE(dueDate));
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function create(form: UpsertTemporaryEmployeeRM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.post(API.V1.RESOURCES.TEMPORARY_EMPLOYEE.CREATE, form);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function update(employeeId: number, form: UpsertTemporaryEmployeeRM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.put(API.V1.RESOURCES.TEMPORARY_EMPLOYEE.EDIT(employeeId), form);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function remove(employeeId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.delete(API.V1.RESOURCES.TEMPORARY_EMPLOYEE.DELETE(employeeId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function bulkRemove(employeeIds: number[]) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.delete(API.V1.RESOURCES.TEMPORARY_EMPLOYEE.BULK_DELETE, { employeeIds });
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function reactivate(employeeId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.patch(API.V1.RESOURCES.TEMPORARY_EMPLOYEE.REACTIVATE(employeeId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function isNameAvailable(request: TemporaryEmployeeNameAvailableRM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.post<TemporaryEmployeeNameAvailableVM>(API.V1.RESOURCES.TEMPORARY_EMPLOYEE.IS_NAME_AVAILABLE, request);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findRelatedWorkOrders(temporaryEmployeeId: number, tableRequestModel: TableQuery, startDate: Date, endDate: Date) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const tableData = new TableQuery(tableRequestModel);
			const startDateString = TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY);
			const endDateString = TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY);
			return await http.get<RelatedWorkOrdersAPI.W_TemporaryEmployee_FindRelatedWorkOrdersTable_VM>(
				RelatedWorkOrdersAPI.URL(temporaryEmployeeId, startDateString, endDateString, tableData)
			);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}
